export type NewsLocale = "sv" | "en";

const BLOG_URL_BY_LOCALE: Record<NewsLocale, string> = {
	sv: "https://exopen.com/sv/blogg",
	en: "https://exopen.com/en/blog",
};

export const blogUrl = (locale: NewsLocale) => BLOG_URL_BY_LOCALE[locale];

// i18next may hand us a region tag ("en-GB"); the feed only knows sv and en.
export const toNewsLocale = (lang: string): NewsLocale =>
	lang.toLowerCase().startsWith("sv") ? "sv" : "en";

export interface BlogFeedTag {
	slug: string;
	name: string;
}

export interface BlogFeedPost {
	id: string;
	locale: NewsLocale;
	slug: string;
	type: "blog" | "knowledge-bank";
	title: string;
	excerpt: string;
	image_url: string | null;
	image_alt: string | null;
	published_at: string;
	read_time_minutes: number | null;
	featured: boolean;
	cluster: string | null;
	category: BlogFeedTag | null;
	tags: BlogFeedTag[];
	url: string;
}

export interface BlogFeedResponse {
	generated_at: string;
	total: number;
	limit: number;
	offset: number;
	posts: BlogFeedPost[];
}

export type NewsTagVariant = "purple" | "primary" | "warning" | "blue" | "gray";

export interface NewsItem {
	id: string;
	title: string;
	tag: string | undefined;
	tagVariant: NewsTagVariant;
	sourceType: BlogFeedPost["type"];
	publishedAt: string;
	url: string;
}

const TAG_VARIANTS: NewsTagVariant[] = [
	"purple",
	"primary",
	"warning",
	"blue",
	"gray",
];

const tagVariantForSlug = (slug: string): NewsTagVariant => {
	let hash = 0;
	for (const character of slug) {
		hash = (hash + character.codePointAt(0)!) % TAG_VARIANTS.length;
	}
	return TAG_VARIANTS[hash] ?? "gray";
};

const postTag = (post: BlogFeedPost): BlogFeedTag | undefined => {
	const cluster = post.cluster?.trim();
	return (
		post.tags[0] ??
		post.category ??
		(cluster ? { slug: cluster.toLowerCase(), name: cluster } : undefined)
	);
};

export const toNewsItem = (post: BlogFeedPost): NewsItem => {
	const tag = postTag(post);
	return {
		id: post.id,
		title: post.title,
		tag: tag?.name,
		tagVariant: tagVariantForSlug(tag?.slug ?? post.slug),
		sourceType: post.type,
		publishedAt: post.published_at,
		url: post.url,
	};
};

// Cluster values are the same in both feed locales, so the Swedish name filters en too.
const EXCLUDED_CLUSTERS = ["karriär på exopen"];

const isShownInNews = (post: BlogFeedPost): boolean =>
	Boolean(postTag(post)) &&
	!EXCLUDED_CLUSTERS.includes(post.cluster?.trim().toLowerCase() ?? "");

export const toNewsItems = (posts: BlogFeedPost[], limit: number): NewsItem[] =>
	posts.filter(isShownInNews).slice(0, limit).map(toNewsItem);
